//src/components/MaterialDatabaseExplorer.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import OekobaudatExplorer from "./OekobaudatExplorer";
import Materials2050Explorer from "./Materials2050Explorer";
import OpenEpdExplorer from "./OpenEpdExplorer";
import EnvirondecExplorer from "./EnvirondecExplorer";
import { Database, ChevronDown, Plus, Loader2, RefreshCw, Info } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "./ui/dialog";

const CUSTOM_EPD_LIBRARY_KEY = "embodix_custom_epds_v1";

const MaterialDatabaseExplorer = ({ onShortlist, shortlist = [], editEpd = null, onEditComplete = null }) => {
  const [source, setSource] = useState("oekobaudat");
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [oekobaudatStatus, setOekobaudatStatus] = useState({ status: "loading", error: null, meta: null });
  const [materials2050Status, setMaterials2050Status] = useState({ status: "loading", error: null, meta: null });
  const [openEpdStatus, setOpenEpdStatus] = useState({ status: "loading", error: null, meta: null });
  const [epdInternationalStatus, setEpdInternationalStatus] = useState({ status: "loading", error: null });
  const oekobaudatRefreshRef = useRef(null);
  const materials2050RefreshRef = useRef(null);
  const openEpdRefreshRef = useRef(null);
  const epdInternationalRefreshRef = useRef(null);

  const handleOekobaudatStatus = useCallback(({ status, error, meta }) => {
    setOekobaudatStatus((prev) => ({
      status: status || prev.status,
      error: error ?? prev.error,
      meta: meta ?? prev.meta
    }));
  }, []);

  const handleMaterials2050Status = useCallback(({ status, error, meta }) => {
    setMaterials2050Status((prev) => ({
      status: status || prev.status,
      error: error ?? prev.error,
      meta: meta ?? prev.meta
    }));
  }, []);

  const handleOpenEpdStatus = useCallback(({ status, error, meta }) => {
    setOpenEpdStatus((prev) => ({
      status: status || prev.status,
      error: error ?? prev.error,
      meta: meta ?? prev.meta
    }));
  }, []);

  const handleEpdInternationalStatus = useCallback(({ status, error }) => {
    setEpdInternationalStatus((prev) => ({
      status: status || prev.status,
      error: error ?? prev.error
    }));
  }, []);

  const registerOekobaudatRefresh = useCallback((fn) => {
    oekobaudatRefreshRef.current = typeof fn === "function" ? fn : null;
  }, []);

  const registerMaterials2050Refresh = useCallback((fn) => {
    materials2050RefreshRef.current = typeof fn === "function" ? fn : null;
  }, []);

  const registerOpenEpdRefresh = useCallback((fn) => {
    openEpdRefreshRef.current = typeof fn === "function" ? fn : null;
  }, []);

  const registerEpdInternationalRefresh = useCallback((fn) => {
    epdInternationalRefreshRef.current = typeof fn === "function" ? fn : null;
  }, []);
  const freshDraft = () => ({
    name: "",
    manufacturer: "",
    declaredUnit: "kg",
    geography: "",
    a1: "",
    a2: "",
    a3: "",
    a1a3: "",
    a4: "",
    a5: "",
    b1: "",
    b2: "",
    b3: "",
    b4: "",
    b5: "",
    b6: "",
    b7: "",
    c1: "",
    c2: "",
    c3: "",
    c4: "",
    d: "",
    densityKgPerM3: "",
    thicknessM: "",
    massPerAreaKgPerM2: "",
    volumePerPieceM3: ""
  });
  const [customEpd, setCustomEpd] = useState(freshDraft);
  const [formError, setFormError] = useState("");
  const [a1a3Manual, setA1a3Manual] = useState(false);

  useEffect(() => {
    if (!editEpd) return;
    const du = editEpd?.declaredUnit;
    const declaredUnit =
      typeof du === "string" ? du : du?.unit || editEpd?.declared_unit || editEpd?.unit || "kg";
    const modules = editEpd?.modules || {};
    const getMod = (k) => {
      const v = modules?.[k];
      if (v == null) return "";
      if (typeof v === "number") return String(v);
      if (typeof v === "object") return String(v.value ?? v.total ?? v.mean ?? "");
      return String(v);
    };

    const props = editEpd?.materialProperties || {};
    const getProp = (k) => {
      const v = props?.[k];
      if (v == null) return "";
      if (typeof v === "number") return String(v);
      if (typeof v === "object") return String(v.value ?? v.amount ?? "");
      return String(v);
    };

    const hasParts = [getMod("A1"), getMod("A2"), getMod("A3")].some((v) => String(v).trim() !== "");
    const hasA1A3 = String(getMod("A1-A3")).trim() !== "";

    setCustomEpd({
      ...freshDraft(),
      name: editEpd?.name || "",
      manufacturer: editEpd?.manufacturer || "",
      geography: editEpd?.geography || "",
      declaredUnit,
      a1: getMod("A1"),
      a2: getMod("A2"),
      a3: getMod("A3"),
      a1a3: getMod("A1-A3"),
      a4: getMod("A4"),
      a5: getMod("A5"),
      b1: getMod("B1"),
      b2: getMod("B2"),
      b3: getMod("B3"),
      b4: getMod("B4"),
      b5: getMod("B5"),
      b6: getMod("B6"),
      b7: getMod("B7"),
      c1: getMod("C1"),
      c2: getMod("C2"),
      c3: getMod("C3"),
      c4: getMod("C4"),
      d: getMod("D"),
      densityKgPerM3: getProp("densityKgPerM3"),
      thicknessM: getProp("thicknessM"),
      massPerAreaKgPerM2: getProp("massPerAreaKgPerM2"),
      volumePerPieceM3: getProp("volumePerPieceM3")
    });
    setA1a3Manual(!hasParts && hasA1A3);
    setEditingId(editEpd?.uuid || editEpd?.id || null);
    setCreateOpen(true);
  }, [editEpd]);

  const moduleSumPreview = useMemo(() => {
    const parseNum = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : null;
    };
    const a1 = parseNum(customEpd.a1);
    const a2 = parseNum(customEpd.a2);
    const a3 = parseNum(customEpd.a3);
    const a1a3 = parseNum(customEpd.a1a3);
    const hasParts = a1 != null || a2 != null || a3 != null;
    const base = [
      customEpd.a4,
      customEpd.a5,
      customEpd.b1,
      customEpd.b2,
      customEpd.b3,
      customEpd.b4,
      customEpd.b5,
      customEpd.b6,
      customEpd.b7,
      customEpd.c1,
      customEpd.c2,
      customEpd.c3,
      customEpd.c4,
      customEpd.d
    ]
      .map(parseNum)
      .filter((v) => Number.isFinite(v));

    const vals = [...base];
    if (hasParts) {
      if (a1 != null) vals.push(a1);
      if (a2 != null) vals.push(a2);
      if (a3 != null) vals.push(a3);
    } else if (a1a3 != null) {
      vals.push(a1a3);
    }

    if (!vals.length) return null;
    return vals.reduce((acc, v) => acc + v, 0);
  }, [customEpd]);

  const loadCustomLibrary = () => {
    try {
      const raw = localStorage.getItem(CUSTOM_EPD_LIBRARY_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      console.error("Failed to read custom EPD library", err);
      return [];
    }
  };

  const saveCustomLibrary = (next) => {
    try {
      localStorage.setItem(CUSTOM_EPD_LIBRARY_KEY, JSON.stringify(next));
    } catch (err) {
      console.error("Failed to persist custom EPD library", err);
    }
  };

  const upsertCustomEpd = (payload) => {
    if (!payload?.uuid) return;
    const current = loadCustomLibrary();
    const exists = current.some((item) => item.uuid === payload.uuid);
    const next = exists
      ? current.map((item) => (item.uuid === payload.uuid ? { ...item, ...payload } : item))
      : [...current, payload];
    saveCustomLibrary(next);
  };

  const resetDraft = () => {
    setCustomEpd(freshDraft());
    setFormError("");
  };

  const handleCreateCustomEpd = (e) => {
    e?.preventDefault?.();
    const name = (customEpd.name || "").trim();
    if (!name) {
      setFormError("Name is required.");
      return;
    }

    const parseNum = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : null;
    };

    const modules = {};
    const addStage = (key, value) => {
      const num = parseNum(value);
      if (num == null) return;
      modules[key] = num;
    };

    const a1 = parseNum(customEpd.a1);
    const a2 = parseNum(customEpd.a2);
    const a3 = parseNum(customEpd.a3);
    const a1a3 = parseNum(customEpd.a1a3);
    const hasParts = a1 != null || a2 != null || a3 != null;

    if (hasParts) {
      addStage("A1", customEpd.a1);
      addStage("A2", customEpd.a2);
      addStage("A3", customEpd.a3);
    } else {
      addStage("A1-A3", customEpd.a1a3);
    }
    addStage("A4", customEpd.a4);
    addStage("A5", customEpd.a5);
    addStage("B1", customEpd.b1);
    addStage("B2", customEpd.b2);
    addStage("B3", customEpd.b3);
    addStage("B4", customEpd.b4);
    addStage("B5", customEpd.b5);
    addStage("B6", customEpd.b6);
    addStage("B7", customEpd.b7);
    addStage("C1", customEpd.c1);
    addStage("C2", customEpd.c2);
    addStage("C3", customEpd.c3);
    addStage("C4", customEpd.c4);
    addStage("D", customEpd.d);

    const modulesTotal =
      Object.keys(modules).length > 0 ? Object.values(modules).reduce((acc, val) => acc + val, 0) : null;
    const gwpTotal = modulesTotal != null ? modulesTotal : 0;

    const materialProperties = {};
    const addProp = (key, value) => {
      const num = parseNum(value);
      if (num == null) return;
      materialProperties[key] = num;
    };
    addProp("densityKgPerM3", customEpd.densityKgPerM3);
    addProp("thicknessM", customEpd.thicknessM);
    addProp("massPerAreaKgPerM2", customEpd.massPerAreaKgPerM2);
    addProp("volumePerPieceM3", customEpd.volumePerPieceM3);

    const payload = {
      uuid: editingId || `custom-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      name,
      declaredUnit: customEpd.declaredUnit
        ? { unit: customEpd.declaredUnit.trim(), amount: 1 }
        : null,
      gwpTotal,
      modules: Object.keys(modules).length ? modules : null,
      geography: (customEpd.geography || "").trim() || null,
      subtype: "custom",
      manufacturer: (customEpd.manufacturer || "").trim() || null,
      source: "custom",
      materialProperties: Object.keys(materialProperties).length ? materialProperties : null,
      __update: !!editingId
    };

    upsertCustomEpd(payload);
    if (typeof onShortlist === "function") onShortlist(payload);
    setCreateOpen(false);
    resetDraft();
    setEditingId(null);
    setA1a3Manual(false);
    onEditComplete?.();
  };

  const handleDialogChange = (open) => {
    setCreateOpen(open);
    if (!open) {
      resetDraft();
      setEditingId(null);
      setA1a3Manual(false);
      onEditComplete?.();
    }
  };

  const sourceControls = (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-gray-900 font-semibold text-base w-full justify-center sm:w-auto sm:justify-start mt-4">
          <Database className="w-4 h-4 text-emerald-600" />
          <span className="text-center sm:text-left">Material databases</span>
        </div>

        <div className="w-full sm:w-auto flex justify-center sm:justify-start mb-3 sm:mb-0">
        <Dialog open={createOpen} onOpenChange={handleDialogChange}>
          <DialogTrigger asChild>
            <Button
              type="button"
              className="h-8 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 shadow-none w-full sm:w-auto text-xs sm:mt-2"
            >
              <Plus className="w-4 h-4 mr-1" />
              Create your own EPD
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl w-[96vw] sm:w-[92vw] max-h-[85vh] overflow-hidden px-4 sm:px-6">
            <DialogHeader className="space-y-1 text-gray-900">
              <DialogTitle className="text-gray-900 text-center mb-2">Create your own EPD</DialogTitle>
              <DialogDescription className="text-xs text-gray-700 text-center">
                Enter a declared unit and any modules you know. Saved entries are added straight to your shortlist so you can map them to layers.
              </DialogDescription>
            </DialogHeader>

            <form className="space-y-3 overflow-y-auto pr-1 max-h-[70vh]" onSubmit={handleCreateCustomEpd}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <label className="space-y-1 text-sm text-gray-900">
                  <span className="text-xs text-gray-700">Product name</span>
                  <Input
                    value={customEpd.name}
                    onChange={(e) => setCustomEpd((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Custom insulation board"
                    required
                    className="h-8 text-xs"
                  />
                </label>
                <label className="space-y-1 text-sm text-gray-900">
                  <span className="text-xs text-gray-700">Manufacturer (optional)</span>
                  <Input
                    value={customEpd.manufacturer}
                    onChange={(e) => setCustomEpd((prev) => ({ ...prev, manufacturer: e.target.value }))}
                    placeholder="Brand or supplier"
                    className="h-8 text-xs"
                  />
                </label>
                <label className="space-y-1 text-sm text-gray-900">
                  <span className="text-xs text-gray-700">Declared unit</span>
                  <Select
                    value={customEpd.declaredUnit}
                    onValueChange={(value) => setCustomEpd((prev) => ({ ...prev, declaredUnit: value }))}
                  >
                    <SelectTrigger className="h-8 w-full border-emerald-200 bg-white px-2 text-xs text-gray-900">
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                    <SelectContent className="border border-emerald-200 bg-white text-gray-900">
                      {["kg", "m2", "m3", "pcs"].map((opt) => (
                        <SelectItem key={opt} value={opt} className="text-xs text-gray-900">
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
                <label className="space-y-1 text-sm text-gray-900">
                  <span className="text-xs text-gray-700">Geography (optional)</span>
                  <Input
                    value={customEpd.geography}
                    onChange={(e) => setCustomEpd((prev) => ({ ...prev, geography: e.target.value }))}
                    placeholder="e.g., US, DE, UK"
                    className="h-8 text-xs"
                  />
                </label>
              </div>

              <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-2 space-y-2">
                <p className="text-xs text-emerald-900 font-semibold">GWP by module (per declared unit)</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                  {[
                    ["A1", "a1"],
                    ["A2", "a2"],
                    ["A3", "a3"],
                    ["A1-A3", "a1a3"],
                    ["A4", "a4"],
                    ["A5", "a5"],
                    ["B1", "b1"],
                    ["B2", "b2"],
                    ["B3", "b3"],
                    ["B4", "b4"],
                    ["B5", "b5"],
                    ["B6", "b6"],
                    ["B7", "b7"],
                    ["C1", "c1"],
                    ["C2", "c2"],
                    ["C3", "c3"],
                    ["C4", "c4"],
                    ["D", "d"]
                  ].map(([label, key]) => (
                    <label key={key} className="space-y-1 text-xs text-gray-900">
                      <span className="text-[11px] text-gray-700">{label}</span>
                      <Input
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        value={customEpd[key]}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          if (key === "a1a3") {
                            setCustomEpd((prev) => ({
                              ...prev,
                              a1a3: nextValue,
                              a1: nextValue ? "" : prev.a1,
                              a2: nextValue ? "" : prev.a2,
                              a3: nextValue ? "" : prev.a3
                            }));
                            setA1a3Manual(!!nextValue);
                            return;
                          }
                          if (key === "a1" || key === "a2" || key === "a3") {
                            setCustomEpd((prev) => {
                              const updated = { ...prev, [key]: nextValue };
                              const a1 = parseFloat(updated.a1);
                              const a2 = parseFloat(updated.a2);
                              const a3 = parseFloat(updated.a3);
                              const hasParts = Number.isFinite(a1) || Number.isFinite(a2) || Number.isFinite(a3);
                              if (hasParts) {
                                const sum = (Number.isFinite(a1) ? a1 : 0) + (Number.isFinite(a2) ? a2 : 0) + (Number.isFinite(a3) ? a3 : 0);
                                updated.a1a3 = String(sum);
                              } else {
                                updated.a1a3 = "";
                              }
                              return updated;
                            });
                            setA1a3Manual(false);
                            return;
                          }
                          setCustomEpd((prev) => ({ ...prev, [key]: nextValue }));
                        }}
                        placeholder="0.00"
                        className="h-7 text-[11px] px-2"
                        disabled={
                          key === "a1a3"
                            ? (customEpd.a1 || customEpd.a2 || customEpd.a3)
                            : key === "a1" || key === "a2" || key === "a3"
                            ? a1a3Manual
                            : false
                        }
                      />
                    </label>
                  ))}
                </div>
                <div className="text-xs text-gray-800 flex flex-wrap items-center gap-2">
                  <span>Total GWP</span>
                  <span className="font-semibold text-emerald-800">
                    {moduleSumPreview != null ? moduleSumPreview.toFixed(2) : "-"}
                  </span>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-2 space-y-2">
                <p className="text-xs text-gray-900 font-semibold">Material properties (optional)</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  <label className="space-y-1 text-xs text-gray-900">
                    <span className="text-[11px] text-gray-700">Density (kg/m³)</span>
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      value={customEpd.densityKgPerM3}
                      onChange={(e) => setCustomEpd((prev) => ({ ...prev, densityKgPerM3: e.target.value }))}
                      placeholder="e.g., 120"
                      className="h-7 text-[11px] px-2"
                    />
                  </label>
                  <label className="space-y-1 text-xs text-gray-900">
                    <span className="text-[11px] text-gray-700">Thickness (m)</span>
                    <Input
                      type="number"
                      step="0.001"
                      inputMode="decimal"
                      value={customEpd.thicknessM}
                      onChange={(e) => setCustomEpd((prev) => ({ ...prev, thicknessM: e.target.value }))}
                      placeholder="e.g., 0.1"
                      className="h-7 text-[11px] px-2"
                    />
                  </label>
                  <label className="space-y-1 text-xs text-gray-900">
                    <span className="text-[11px] text-gray-700">Mass per area (kg/m²)</span>
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      value={customEpd.massPerAreaKgPerM2}
                      onChange={(e) => setCustomEpd((prev) => ({ ...prev, massPerAreaKgPerM2: e.target.value }))}
                      placeholder="e.g., 15"
                      className="h-7 text-[11px] px-2"
                    />
                  </label>
                  <label className="space-y-1 text-xs text-gray-900">
                    <span className="text-[11px] text-gray-700">Volume per piece (m³)</span>
                    <Input
                      type="number"
                      step="0.0001"
                      inputMode="decimal"
                      value={customEpd.volumePerPieceM3}
                      onChange={(e) => setCustomEpd((prev) => ({ ...prev, volumePerPieceM3: e.target.value }))}
                      placeholder="e.g., 0.02"
                      className="h-7 text-[11px] px-2"
                    />
                  </label>
                </div>
              </div>

              <DialogFooter className="items-center">
                {formError && <div className="text-sm text-red-600 mr-auto">{formError}</div>}
                <Button
                  type="button"
                  variant="outline"
                  className="text-gray-900"
                  onClick={() => handleDialogChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-emerald-600 text-white hover:bg-emerald-700">
                  Save to shortlist
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <p
        className="text-sm text-gray-600 text-justify sm:text-left sm:mt-0"
        style={{ marginTop: "2px", marginBottom: "16px" }}
      >
        Choose a database source. Search results and details will load from the selected source.
      </p>
      <div className="flex items-center gap-3 flex-wrap" style={{ marginBottom: "4px" }}>
        <label className="text-xs text-gray-600 uppercase tracking-wide">Database</label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="h-7 min-w-[120px] justify-between border-emerald-200 bg-white text-emerald-800 shadow-sm px-2 text-xs"
            >
              <span className="font-medium">
                {source === "oekobaudat"
                  ? "OEKOBAUDAT"
                  : source === "2050"
                  ? "2050 Materials"
                  : source === "epd-international"
                  ? "EPD International"
                  : "OpenEPD (Work in progress)"}
              </span>
              <ChevronDown className="w-4 h-4 text-emerald-600" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[220px]">
            <DropdownMenuLabel className="text-xs font-semibold text-emerald-700">Database</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-xs"
              onSelect={(e) => {
                setSource("oekobaudat");
              }}
            >
              OEKOBAUDAT
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-xs"
              onSelect={(e) => {
                setSource("2050");
              }}
            >
              2050 Materials
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-xs"
              onSelect={(e) => {
                setSource("openepd");
              }}
            >
              OpenEPD (Work in progress)
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-xs"
              onSelect={(e) => {
                setSource("epd-international");
              }}
            >
              EPD International (Environdec)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {(() => {
          const statusConfig =
            source === "oekobaudat"
              ? {
                  status: oekobaudatStatus.status,
                  error: oekobaudatStatus.error,
                  refreshRef: oekobaudatRefreshRef,
                  label: "OEKOBAUDAT",
                  successText: "Databases loaded from OEKOBAUDAT",
                  loadingText: "Connecting to OEKOBAUDAT...",
                  errorText: "OEKOBAUDAT request failed",
                  tooltip: oekobaudatStatus.meta?.datastock
                    ? `Datastock: ${oekobaudatStatus.meta.datastock?.shortName || "Unavailable"}${
                        oekobaudatStatus.meta.datastock?.uuid
                          ? `\nUUID: ${oekobaudatStatus.meta.datastock.uuid}`
                          : ""
                      }`
                    : null
                }
              : source === "2050"
              ? {
                  status: materials2050Status.status,
                  error: materials2050Status.error,
                  refreshRef: materials2050RefreshRef,
                  label: "2050 Materials",
                  successText: "Databases loaded from 2050 Materials",
                  loadingText: "Connecting to 2050 Materials...",
                  errorText: "2050 Materials request failed",
                  tooltip: materials2050Status.meta?.datasetInfo || null
                }
              : source === "openepd"
              ? {
                  status: openEpdStatus.status,
                  error: openEpdStatus.error,
                  refreshRef: openEpdRefreshRef,
                  label: "OpenEPD",
                  successText: "Connected to OpenEPD",
                  loadingText: "Connecting to OpenEPD...",
                  errorText: "OpenEPD request failed",
                  tooltip: openEpdStatus.meta?.proxyMeta?.headers?.["x-request-id"]
                    ? `OpenEPD request id: ${openEpdStatus.meta.proxyMeta.headers["x-request-id"]}`
                    : openEpdStatus.meta?.proxyMeta?.headers?.["x-amzn-trace-id"]
                    ? `OpenEPD trace: ${openEpdStatus.meta.proxyMeta.headers["x-amzn-trace-id"]}`
                    : openEpdStatus.meta?.proxyMeta?.headers?.date
                    ? `OpenEPD response: ${openEpdStatus.meta.proxyMeta.headers.date}`
                    : openEpdStatus.meta?.proxyMeta?.fetchedAt
                    ? `OpenEPD fetched: ${new Date(openEpdStatus.meta.proxyMeta.fetchedAt).toLocaleString()}`
                    : openEpdStatus.meta?.proxyMeta
                    ? "OpenEPD proxy ready"
                    : null
                }
              : source === "epd-international"
              ? {
                  status: epdInternationalStatus.status,
                  error: epdInternationalStatus.error,
                  refreshRef: epdInternationalRefreshRef,
                  label: "EPD International",
                  successText: "Databases loaded from EPD International",
                  loadingText: "Connecting to EPD International...",
                  errorText: "EPD International request failed",
                  tooltip: "Filters loaded from EPD International."
                }
              : null;

          if (!statusConfig) return null;
          return (
            <div className="flex items-center gap-2 text-xs">
              {statusConfig.status === "loading" ? (
                <div className="flex items-center gap-2 text-emerald-700">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{statusConfig.loadingText}</span>
                </div>
              ) : statusConfig.status === "success" ? (
                <div className="flex items-center gap-2 text-emerald-800">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => statusConfig.refreshRef?.current?.()}
                    className="h-6 w-6 text-emerald-700 hover:text-emerald-900"
                    aria-label={`Refresh ${statusConfig.label}`}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <span className="font-medium">{statusConfig.successText}</span>
                  {statusConfig.tooltip && (
                    <div className="relative group/source flex items-center">
                      <button
                        type="button"
                        className="p-1 text-emerald-700 hover:text-emerald-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-1 focus-visible:ring-offset-white rounded-full"
                        aria-label={`Show ${statusConfig.label} connection details`}
                      >
                        <Info className="w-4 h-4" aria-hidden="true" />
                      </button>
                      <div className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-72 -translate-x-1/2 opacity-0 transition-opacity duration-150 group-hover/source:opacity-100 group-focus-within/source:opacity-100">
                        <div className="relative rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-[11px] text-emerald-700 shadow-sm whitespace-pre-line">
                          {statusConfig.tooltip}
                          <div className="absolute -bottom-1 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-r border-b border-emerald-200 bg-emerald-50" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-600">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => statusConfig.refreshRef?.current?.()}
                    className="h-6 w-6 text-emerald-700 hover:text-emerald-900"
                    aria-label={`Retry ${statusConfig.label}`}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <span>{statusConfig.errorText}</span>
                  {statusConfig.error && <span className="text-xs">{statusConfig.error}</span>}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {sourceControls}
      {source === "oekobaudat" ? (
        <OekobaudatExplorer
          onShortlist={onShortlist}
          shortlist={shortlist}
          onStatusChange={handleOekobaudatStatus}
          onRegisterRefresh={registerOekobaudatRefresh}
        />
      ) : source === "2050" ? (
        <Materials2050Explorer
          onShortlist={onShortlist}
          shortlist={shortlist}
          onStatusChange={handleMaterials2050Status}
          onRegisterRefresh={registerMaterials2050Refresh}
        />
      ) : source === "epd-international" ? (
        <EnvirondecExplorer
          onShortlist={onShortlist}
          shortlist={shortlist}
          onStatusChange={handleEpdInternationalStatus}
          onRegisterRefresh={registerEpdInternationalRefresh}
        />
      ) : (
        <OpenEpdExplorer
          onShortlist={onShortlist}
          shortlist={shortlist}
          onStatusChange={handleOpenEpdStatus}
          onRegisterRefresh={registerOpenEpdRefresh}
        />
      )}
    </div>
  );
};

export default MaterialDatabaseExplorer;
